<template lang="pug">
section#start-blood-sugar
  section.banner-area.relative
    .overlay.overlay-bg
    .container
      .row.fullscreen.align-items-center.justify-content-between
        .col-lg-6.col-md-7
          .banner-content(v-show="parsing")
            full-loader
          .banner-content(v-show="!parsing")
            h1(v-html="$t('startBloodSugar.title')")
            p(v-html="$t('startBloodSugar.subtitle')")
            .box
              input.inputfile#sugarFile(accept=".csv", type="file", name="sugarFile", v-on:change="fileAdd")
              label(v-bind:title="uploaderLabel", v-bind:class="{'primary-btn': !sugarFile && !loading, 'genric-btn link': sugarFile || loading}", for="sugarFile")
                | {{ uploaderLabel | crop(15, '...') }}
                loader(v-show="loading")
                span(v-show="!sugarFile && !loading").lnr.lnr-cloud-upload
              button.primary-btn.next-btn(v-on:click.prevent="fileTreat", v-show="sugarFile")
                | {{ $t('controls.next') }}
                span.lnr.lnr-arrow-right
            span.drive-select(v-show="pickerApiLoaded")
              | {{ $t('controls.or') }}
              | &nbsp;
              a#drive-btn(href="/", v-on:click.prevent="") {{ $t('controls.loadDrive') }}
        .rpanel.col-lg-6.col-md-5
          .overlay.overlay-bg
            .counter
              p.step {{ $t('controls.step') }}
              p
                span.current 1
                span /4
      .row
        .col
          router-link.genric-btn.circle.link(to="/start")
            span.lnr.lnr-arrow-left
            | &nbsp;
            | {{ $t('controls.back') }}

  section.next-area.relative
    .overlay.overlay-bg
    .container
      .row
        .col-md-10.offset-md-1
          p.next-title {{ $t('controls.next') }}: {{ $t('startBloodSugar.next') }}
</template>

<script>
import Project from '@/constants';
import axios from 'axios';
import saveState from 'vue-save-state';
import Loader from '@/components/partials/Loader';
import FullLoaer from '@/components/partials/FullLoader';
import ppp from 'papaparse';

export default {
  name: 'StartBloodSugar',
  components: {
    loader: Loader,
    'full-loader': FullLoaer,
  },
  mixins: [saveState],
  mounted() {
    this.oauthToken = window.oauthToken;
    this.onApiLoad();
    if (this.sugarFile && !this.sugarFile.name) this.sugarFile = null;
  },
  data() {
    return {
      sugarFile: null,
      pickerApiLoaded: false,
      oauthToken: false,
      fromDrive: false,
      loading: false,
      parsing: false,
    };
  },
  computed: {
    uploaderLabel() {
      if (this.loading) return '';
      const prefix = this.fromDrive ? '(drive) ' : '';
      return this.sugarFile ? `${prefix}${this.sugarFile.name}` : this.$t('controls.upload');
    },
  },
  metaInfo() {
    return {
      title: this.$t('startBloodSugar.meta.title'),
    };
  },
  methods: {
    getSaveStateConfig() {
      return {
        cacheKey: 'start.bloodsugar',
        saveProperties: ['sugarFile', 'fromDrive'],
      };
    },
    onApiLoad() {
      if (window.gapi) {
        if (!window.gapi.auth2) window.gapi.load('auth2', this.onAuthApiLoad);
        else this.onAuthApiLoad();
        if (!window.gapi.picker) window.gapi.load('picker', this.onPickerApiLoad);
        else this.pickerApiLoaded = true;
      } else {
        setTimeout(() => {
          this.onApiLoad();
        }, 300);
      }
    },
    onAuthApiLoad() {
      const authBtn = document.getElementById('drive-btn');
      authBtn.disabled = false;
      authBtn.addEventListener('click', () => {
        if (this.oauthToken) this.createPicker();
        else {
          window.gapi.auth2.authorize({
            client_id: Project.google.picker.clientId,
            scope: Project.google.picker.scope,
          }, this.handleAuthResult);
        }
      });
    },
    onPickerApiLoad() {
      this.pickerApiLoaded = true;
      this.createPicker();
    },
    handleAuthResult(authResult) {
      if (authResult && !authResult.error) {
        this.oauthToken = authResult.access_token;
        this.createPicker();
      }
    },
    createPicker() {
      if (this.pickerApiLoaded && this.oauthToken) {
        const picker = new window.google.picker.PickerBuilder()
          .addView(window.google.picker.ViewId.DOCS)
          .setOAuthToken(this.oauthToken)
          .setDeveloperKey(Project.google.picker.developerKey)
          .setCallback(this.pickerCallback)
          .build();
        picker.setVisible(true);
      }
    },
    pickerCallback(data) {
      if (data[window.google.picker.Response.ACTION] === window.google.picker.Action.PICKED) {
        this.loading = true;
        this.sugarFile = null;
        const doc = data[window.google.picker.Response.DOCUMENTS][0];
        const id = doc[window.google.picker.Document.ID];
        const name = doc[window.google.picker.Document.NAME];
        const url = `https://www.googleapis.com/drive/v3/files/${id}?key=${Project.google.picker.developerKey}&access_token=${this.oauthToken}&alt=media`;
        axios.get(url)
          .then((res) => {
            $('#sugarFile').val('');
            this.sugarFile = {
              name,
              data: res.data,
            };
            this.fromDrive = true;
            this.loading = false;
          })
          .catch((err) => {
            console.log(err);
          });
      }
    },
    fileAdd() {
      const selectedFile = document.getElementById('sugarFile').files[0];
      if (!selectedFile) return;
      this.loading = true;
      this.sugarFile = selectedFile;
      this.fromDrive = false;
      const reader = new FileReader();
      reader.onload = (event) => {
        const data = event.target.result;
        const name = this.sugarFile.name;
        this.sugarFile = {
          name,
          data,
        };
      };
      reader.onloadend = () => {
        this.loading = false;
      };
      reader.readAsText(selectedFile);
    },
    fileTreat() {
      window.oauthToken = this.oauthToken;
      this.parsing = true;
      const splits = this.sugarFile.data.split('\n');
      const headers = splits[0].split('\t');
      const sanitizedHeaders = [];
      headers.forEach((header) => {
        sanitizedHeaders.push(header.replace(/ /g, '')
          .replace(/-/g, '')
          .replace(/\(grams\)/g, '')
          .replace(/\(mmol\/L\)/g, '')
          .replace(/\(units\)/g, 'Units'));
      });
      const header = sanitizedHeaders.join('\t');
      const fileStr = `${header}\n${splits.slice(1).join('\n')}`;
      ppp.parse(fileStr, {
        delimiter: '\t',
        header: true,
        comments: false,
        skipEmptyLines: true,
        fastMode: true,
        complete: this.filterData,
      });
    },
    filterData(data) {
      this.$store.firstDate = new Date(data.data[0].Time);
      this.$store.lastDate = new Date(data.data[data.data.length - 1].Time);
      this.$store.sugarData.getCollection('entries').clear();
      this.$store.sugarData.getCollection('entries').insert(data.data);
      setTimeout(() => {
        this.$router.push('/start/meals');
      }, 600);
    },
  },
};
</script>

<style lang="stylus" scoped>
@import '../../assets/css/variables.styl'

#start-blood-sugar
  overflow hidden
  background white
  .banner-content
    padding-right base-padding

    .loader
      position relative
      top 4px

  .next-btn
    margin-left 15px

  .drive-select
    margin-left 15px
    a
      font-variant small-caps
      text-transform uppercase
      font-size .9em
      transition color ease .2s
      &:hover
        color color-main

.next-area
  background color-main
  padding base-padding * 2
  background url(/static/img/hypnotize.png) repeat
  .overlay-bg
    padding base-padding
    background rgba(color-main, .8)
  p
    color white
    font-size 2.5em
    font-weight 200
    text-align center
    font-variant small-caps
    text-transform lowercase
    letter-spacing 0.2em
    line-height 1em

.banner-area
  padding-bottom base-padding
  //border-bottom 1px solid rgba(color-base, .25)

.rpanel
  background url(/static/img/doctor2.jpg) no-repeat center/cover
  //width 50%
  height 100%
  top 0px
  right 0
  position absolute

  .overlay-bg
    background rgba(226, 109, 92, 0.8)
    display flex
    align-items center
    text-align center

  .counter
    margin auto
    color white
    font-size 15em
    span:not(.current)
      font-weight 200
      opacity .75
    .step
      font-size 0.2em
      font-weight 200
      font-variant small-caps
      text-transform lowercase
      letter-spacing 0.3em
      position relative
      top -120px
    .current
      font-weight 900

@media (max-width: size-sm)
  .rpanel
    display none
    visibility hidden
</style>



// WEBPACK FOOTER //
// src/components/pages/Start.BloodSugar.vue